/** Maps current direct study status and cleanup receipts to Tests cards. No legacy identity or status merging. */
import { studySummaryPresentation } from '../../hooks/study-builder/studyAuthoringPresentation'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { SavedStudyBuilderDraftSummary } from '../../hooks/study-builder/studyBuilderDrafts'
import { formatDateTime } from '../../utils/formatters'
import type { TestsIndexAction, TestsIndexRecord, TestsIndexRecords } from './types'

const copy = shopifyComponentCopy.testsIndex
export interface TestsIndexRecordInputs {
  drafts: readonly SavedStudyBuilderDraftSummary[]
  canAuthor: boolean
}
function dateLabel(template: string, value: string | null | undefined) {
  const formatted = formatDateTime(value, '')
  return formatted ? template.replace('{date}', formatted) : null
}
function timeValue(value: string | null | undefined) {
  const parsed = Date.parse(value ?? '')
  return Number.isNaN(parsed) ? 0 : parsed
}
function readText(value: string | null | undefined) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** One record per direct study, including a retained cleanup receipt when authoring is gone. */
export function buildStudyRecord(draft: SavedStudyBuilderDraftSummary, canAuthor: boolean): TestsIndexRecord {
  const draftId = readText(draft.studyId)
  if (!draftId) throw new Error('STUDY_AUTHORING_INVALID: Missing study identity')
  const productTitle = readText(draft.productTitle)
  const status = studySummaryPresentation(draft)
  const action: TestsIndexAction = canAuthor
    ? { kind: 'open-study', studyId: draftId, status: draft.state?.status ?? 'deleting' }
    : { kind: 'read-only', reason: copy.readOnlyAuthoringUnavailable }
  const detail = canAuthor ? null : copy.readOnlyAuthoringUnavailable
  return {
    key: `study:${draftId}`,
    source: 'study',
    title: readText(draft.title) ?? productTitle ?? copy.untitledTest,
    productTitle,
    kindLabel: null,
    imageUrl: readText(draft.productImageUrl),
    imageAlt: readText(draft.productImageAlt) ?? productTitle ?? '',
    statusLabel: status.label,
    statusTone: status.tone,
    timeLabel: dateLabel(copy.savedTimeLabel, draft.updatedAt),
    detail,
    runId: draft.state?.latestRunId ?? null,
    progress: null,
    stage: null,
    statusIssue: null,
    action,
  }
}

export function buildTestsIndexRecords(inputs: TestsIndexRecordInputs): TestsIndexRecords {
  const ordered = inputs.drafts.toSorted(
    (left, right) =>
      timeValue(right.updatedAt) - timeValue(left.updatedAt) || left.draftId.localeCompare(right.draftId),
  )
  const saved: TestsIndexRecord[] = []
  const active: TestsIndexRecord[] = []
  const results: TestsIndexRecord[] = []
  for (const study of ordered) {
    const record = buildStudyRecord(study, inputs.canAuthor)
    if (study.state?.status === 'running') active.push(record)
    else if (study.state?.status === 'complete') results.push(record)
    else saved.push(record)
  }
  return { saved, active, results }
}
